import {createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';

import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ConfigurationPanelGalleryBackIcon} from '@amblique/king-living/app/components/configuration-selector/configuration-panel-gallery-back-icon';
import {ConfigurationPanelStepBackIcon} from '@amblique/king-living/app/components/configuration-selector/configuration-panel-step-back-icon';
import ReviewSection from '@amblique/king-living/app/components/configuration-selector/review-section';
import {getCoverGroups} from '@amblique/king-living/app/utils/customize-selections';
import {mergePreselectedSelectionsFromProduct} from '@amblique/king-living/app/utils/customize-selections';
import {sumSelectionPrices, sumSelectionListPrices} from '@amblique/king-living/app/components/complex-customize/utils/selection-utils';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {useVisualViewportRect} from '@amblique/king-living/app/hooks/use-visual-viewport-rect';
import {useMobilePdpDrawerScrollLock} from '@amblique/king-living/app/hooks/use-mobile-pdp-drawer-scroll-lock';
import {focusPdpPanelInitialTarget, usePdpConfigPanelA11y} from '@amblique/king-living/app/hooks/use-pdp-config-panel-a11y';
import {CustomizeStepsContent} from '@amblique/king-living/app/components/complex-customize/customize-steps-content';
import {PDP_DESKTOP_CONFIG_PANEL_WIDTH_PX} from '@amblique/king-living/app/constants/pdp-layout';
import {
  buildLayerFromProduct,
  buildLayersFromSelections,
  resolveViewDetailsOptionGalleryResource,
} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {getGalleryDetailShortLabel} from '@amblique/king-living/app/utils/utils';

const CustomizeStepsInlineContext = createContext(null);

function useCustomizeStepsInlineContext() {
  const context = useContext(CustomizeStepsInlineContext);

  if (!context) {
    throw new Error('CustomizeStepsInline must be used within CustomizeStepsInlineProvider');
  }

  return context;
}

function useCustomizeStepsInlineSession({
  product,
  autoCommit = false,
  coverVariantProduct,
  committedOptionProduct,
  mobileGalleryLayers,
  mobileGalleryPreviewProduct,
  mobileGalleryVariationAttributes,
  onCoverCommitted,
  onCoverPickerOpen,
  onGalleryProductChange,
  onPickerOpenChange,
  onSlotProductPreview,
  onViewDetails,
  closePickerRef,
}) {
  const intl = useIntl();
  const {committedSelection, commitInlineSelections, setInlineSelectionsDraft} = useCustomizeJourney();
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const inlineStyles = useMultiStyleConfig('CustomizeStepsInline', {});
  const isMobileBreakpoint = useBreakpointValue({base: true, lg: false});
  const isMobile = isMobileBreakpoint === true;

  const coverGroups = useMemo(() => getCoverGroups(product), [product]);

  const committedSelectionsSyncKey = useMemo(
    () => JSON.stringify(committedSelection.selections ?? {}),
    [committedSelection.selections]
  );

  const [inlineDraft, setInlineDraft] = useState(() => mergePreselectedSelectionsFromProduct(product, committedSelection.selections, {disablePreselect: true}));

  useLayoutEffect(() => {
    setInlineSelectionsDraft(inlineDraft);
  }, [inlineDraft, setInlineSelectionsDraft]);

  useEffect(
    () => () => setInlineSelectionsDraft(null),
    [setInlineSelectionsDraft]
  );

  useEffect(() => {
    let selections = {};

    try {
      selections = JSON.parse(committedSelectionsSyncKey);
    } catch {
      selections = {};
    }

    const merged = mergePreselectedSelectionsFromProduct(product, selections, {disablePreselect: true});

    setInlineDraft((prev) => {
      try {
        if (JSON.stringify(prev) === JSON.stringify(merged)) return prev;
      } catch {
        // fall through to replace
      }

      return merged;
    });
  }, [product, committedSelectionsSyncKey]);

  const subtotalPrice = useMemo(
    () => sumSelectionPrices(inlineDraft),
    [inlineDraft]
  );

  const subtotalListPrice = useMemo(
    () => sumSelectionListPrices(inlineDraft),
    [inlineDraft]
  );

  const [activeSlot, setActiveSlot] = useState(null);
  const isSubViewOpen = activeSlot !== null;
  const [galleryViewState, setGalleryViewState] = useState(null);
  const drawerViewportRect = useVisualViewportRect(Boolean(isMobile && isSubViewOpen));
  const {handleDrawerCloseComplete} = useMobilePdpDrawerScrollLock(isSubViewOpen, isMobile);

  useEffect(() => {
    onPickerOpenChange?.(isSubViewOpen);
  }, [isSubViewOpen, onPickerOpenChange]);

  useEffect(() => {
    if (!isSubViewOpen) {
      setGalleryViewState(null);
    }
  }, [isSubViewOpen]);

  const [panelHeaderInfo, setPanelHeaderInfo] = useState(null);
  const desktopPickerBackRef = useRef(null);

  const handleSelect = useCallback((stepId, value) => {
    setInlineDraft((prev) => {
      const next = {...prev, [stepId]: value};

      if (autoCommit) {
        Promise.resolve().then(() => {
          commitInlineSelections(next);

          if (stepId === 'cover') {
            onCoverCommitted?.();
          }
        });
      }

      return next;
    });
  }, [autoCommit, commitInlineSelections, onCoverCommitted]);

  const handleDone = useCallback(() => {
    commitInlineSelections(inlineDraft);
  }, [commitInlineSelections, inlineDraft]);

  const handleCloseSubView = useCallback(() => {
    setActiveSlot(null);
  }, []);

  useEffect(() => {
    if (!closePickerRef) return undefined;

    closePickerRef.current = handleCloseSubView;

    return () => {
      closePickerRef.current = null;
    };
  }, [closePickerRef, handleCloseSubView]);

  const fallbackGalleryLayers = useMemo(() => {
    const base = buildLayersFromSelections(inlineDraft);
    const previewProduct = mobileGalleryPreviewProduct;

    if (!previewProduct || previewProduct === product) return base;

    return [...base, buildLayerFromProduct(previewProduct)];
  }, [inlineDraft, mobileGalleryPreviewProduct, product]);

  const inlineGalleryLayers = mobileGalleryLayers ?? fallbackGalleryLayers;
  const inlineGalleryPreviewProduct = mobileGalleryPreviewProduct ?? product;
  const inlineGalleryVariationAttributes = mobileGalleryVariationAttributes ?? {};

  const handleExpandInlineGallery = useCallback(() => {
    setGalleryViewState({
      product: inlineGalleryPreviewProduct,
      selectedVariationAttributes: inlineGalleryVariationAttributes,
    });
  }, [inlineGalleryPreviewProduct, inlineGalleryVariationAttributes]);

  const handleMobilePickerViewDetails = useCallback((pickerProduct, options = {}) => {
    if (!pickerProduct) return;

    const {shelvesDetailUi = false, coverItemType} = options;
    const clickedVariation = pickerProduct._selectedVariationValues;

    setGalleryViewState({
      product: pickerProduct,
      selectedVariationAttributes: clickedVariation && Object.keys(clickedVariation).length > 0 ?
        clickedVariation :
        inlineGalleryVariationAttributes,
      shelvesDetailUi: Boolean(shelvesDetailUi),
      coverItemType: typeof coverItemType === 'string' && coverItemType.trim() !== '' ?
        coverItemType.trim() :
        undefined,
    });
  }, [inlineGalleryVariationAttributes]);

  const handleCloseGalleryView = useCallback(() => {
    setGalleryViewState(null);
  }, []);

  const pickerViewDetailsGalleryResourceOverride = useMemo(() => {
    if (!galleryViewState?.product) return null;

    return resolveViewDetailsOptionGalleryResource(
      galleryViewState.product,
      product,
      galleryViewState.selectedVariationAttributes ?? inlineGalleryVariationAttributes
    );
  }, [galleryViewState, inlineGalleryVariationAttributes, product]);

  const galleryViewTitle = useMemo(() => {
    if (!galleryViewState) {
      return intl.formatMessage({
        defaultMessage: 'View layout details',
        id: 'configurationPanel.galleryDetailsTitle',
      });
    }

    if (galleryViewState.coverItemType) {
      return intl.formatMessage(
        {
          defaultMessage: '{label} details',
          id: 'configurationPanel.galleryDetailsTitleWithLabel',
        },
        {label: galleryViewState.coverItemType}
      );
    }

    const detailLabel = getGalleryDetailShortLabel(galleryViewState.product);

    if (galleryViewState.shelvesDetailUi && detailLabel) {
      return intl.formatMessage(
        {
          defaultMessage: '{label} details',
          id: 'configurationPanel.galleryDetailsTitleWithLabel',
        },
        {label: detailLabel}
      );
    }

    return intl.formatMessage({
      defaultMessage: 'View layout details',
      id: 'configurationPanel.galleryDetailsTitle',
    });
  }, [galleryViewState, intl]);

  const propagateGallery = useCallback(
    ({product: galleryProduct, selectedVariationAttributes, viewDetailsProduct} = {}) => {
      onGalleryProductChange?.({
        product: galleryProduct ?? null,
        selectedVariationAttributes: selectedVariationAttributes ?? {},
        viewDetailsProduct: viewDetailsProduct ?? null,
      });
    },
    [onGalleryProductChange]
  );

  const handleGalleryFromMain = useCallback((data) => {
    if (activeSlot !== null) return;
    propagateGallery(data);
  }, [activeSlot, propagateGallery]);

  const handleGalleryFromPicker = useCallback((data) => {
    if (activeSlot === null) return;
    propagateGallery(data);
  }, [activeSlot, propagateGallery]);

  const sharedContentProps = {
    controlledActiveSlot: activeSlot,
    coverGroups,
    coverVariantProduct,
    committedOptionProduct,
    enableLocking: false,
    isSimplePDP: true,
    product,
    selections: inlineDraft,
    showSubtotalFooter: !autoCommit,
    subtotalListPrice,
    subtotalPrice,
    onActiveSlotChange: setActiveSlot,
    onDone: autoCommit ? undefined : handleDone,
    onSelect: handleSelect,
    onCoverPickerOpen,
    onSlotProductPreview,
    onViewDetails,
  };

  const pickerHeaderClassName = [
    'configuration-panel-header',
    'configuration-panel-header--customize-step',
    panelHeaderInfo?.customizeStepKind &&
    `configuration-panel-header--customize-step--${panelHeaderInfo.customizeStepKind}`
  ].filter(Boolean).join(' ');

  const stepBackButton = panelHeaderInfo?.onBack ? (
    <IconButton
      ref={!isMobile ? desktopPickerBackRef : undefined}
      aria-label="Back to steps"
      icon={<ConfigurationPanelStepBackIcon />}
      {...(isMobile ? styles.mobileStepBackButton : styles.desktopPanelBackButton)}
      onClick={panelHeaderInfo.onBack}
    />
  ) : null;

  const stepTitle = panelHeaderInfo ? (
    <Flex {...styles.panelHeaderTitleRow} className="panel-header-title-row" width="100%">
      <Text
        className="panel-header-title"
        {...(isMobile && panelHeaderInfo.chooseYourShelvesStep ?
          styles.panelHeaderTitleLabelShelves :
          styles.panelHeaderTitleLabel)}
        noOfLines={1}
      >
        {panelHeaderInfo.title}
      </Text>
      {panelHeaderInfo.stepIndex != null && panelHeaderInfo.totalSteps != null && (
        <Text className="panel-header-step" {...styles.panelHeaderStepIndicator}>
          {`${panelHeaderInfo.stepIndex}/${panelHeaderInfo.totalSteps}`}
        </Text>
      )}
    </Flex>
  ) : null;

  const mobilePickerStepHeader = (
    <Flex alignItems="center" gap={4} width="100%">
      {stepBackButton}
      {stepTitle}
    </Flex>
  );

  const desktopPickerStepHeader = (
    <>
      {stepBackButton}
      {stepTitle}
    </>
  );

  const pickerContent = (
    <CustomizeStepsContent
      {...sharedContentProps}
      viewMode="picker"
      showSubtotalFooter
      onGalleryProductChange={handleGalleryFromPicker}
      onHeaderChange={setPanelHeaderInfo}
      onViewDetails={isMobile ? handleMobilePickerViewDetails : onViewDetails}
    />
  );

  const configurationPanelBodyWrapperClassName = [
    'configuration-panel-body-wrapper',
    panelHeaderInfo?.customizeStepKind &&
    `configuration-panel-body-wrapper--customize-step--${panelHeaderInfo.customizeStepKind}`
  ].filter(Boolean).join(' ');

  const mobileGalleryHeader = (
    <Box {...styles.mobileImageHeader}>
      <ReviewSection
        layers={inlineGalleryLayers}
        masterProduct={product}
        selectedVariationAttributes={inlineGalleryVariationAttributes}
        showViewDetailsSection={false}
        previewProduct={
          inlineGalleryPreviewProduct !== product ? inlineGalleryPreviewProduct : null
        }
        forceCylindoCheck
        isSimpleProduct
        isThumbnailZoomEnabled
      />
      <IconButton
        {...styles.mobileExpandButton}
        aria-label={intl.formatMessage({
          defaultMessage: 'Expand gallery',
          id: 'configurationPanel.aria.expandGallery',
        })}
        icon={
          <Icon viewBox="0 0 24 24">
            <path
              d="M3 3h7v2H5v5H3V3zm11 0h7v7h-2V5h-5V3zM3 14h2v5h5v2H3v-7zm16 5h-5v2h7v-7h-2v5z"
              fill="currentColor"
            />
          </Icon>
        }
        onClick={handleExpandInlineGallery}
      />
    </Box>
  );

  const mobilePickerHeader = galleryViewState ? (
    <Flex alignItems="center" gap={4} width="100%">
      <IconButton
        {...styles.galleryViewBackButton}
        icon={<ConfigurationPanelGalleryBackIcon />}
        aria-label={intl.formatMessage({
          defaultMessage: 'Back to configuration',
          id: 'configurationPanel.aria.backToConfiguration',
        })}
        onClick={handleCloseGalleryView}
      />
      <Text
        {...(galleryViewState.coverItemType ?
          styles.galleryViewHeaderTitleCoverDetail :
          galleryViewState.shelvesDetailUi ?
            styles.galleryViewHeaderTitleShelvesDetail :
            styles.galleryViewHeaderTitle)}
        noOfLines={1}
      >
        {galleryViewTitle}
      </Text>
    </Flex>
  ) : (
    mobilePickerStepHeader
  );

  const mobilePickerPanel = (
    <Box
      className={[
        'configuration-panel__root',
        galleryViewState ?
          'configuration-panel__root--gallery-detail' :
          'configuration-panel__root--customize-step'
      ].join(' ')}
      {...styles.configurationPanelRoot}
      display="flex"
      flex={1}
      flexDirection="column"
      height="100%"
      minH={0}
      overflow="hidden"
    >
      <Box
        as="header"
        borderBottomWidth={0}
        borderWidth={0}
        flexShrink={0}
        className={[
          'configuration-panel-header',
          galleryViewState ?
            [
              'configuration-panel-header--gallery-detail',
              (galleryViewState.shelvesDetailUi || galleryViewState.coverItemType) ?
                'configuration-panel-header--gallery-shelves-detail' :
                'configuration-panel-header--gallery-layout'
            ].join(' ') :
            pickerHeaderClassName
        ].join(' ')}
        {...(galleryViewState ? styles.galleryViewHeader : styles.header)}
      >
        {mobilePickerHeader}
      </Box>

      {galleryViewState && (
        <Box className="gallery-overlay-content" {...styles.galleryOverlayContent}>
          <ReviewSection
            galleryResourceOverride={pickerViewDetailsGalleryResourceOverride}
            layers={pickerViewDetailsGalleryResourceOverride ? undefined : inlineGalleryLayers}
            masterProduct={product}
            previewProduct={galleryViewState.product}
            selectedVariationAttributes={galleryViewState.selectedVariationAttributes}
            viewDetailsProduct={galleryViewState.product}
            forceCylindoCheck
            hideImageGalleryOverlayButtons
            isSimpleProduct
            isThumbnailZoomEnabled
            showViewDetailsSection
            stickyGallery
          />
        </Box>
      )}

      <Box
        className={configurationPanelBodyWrapperClassName}
        display={galleryViewState ? 'none' : 'flex'}
        flex={galleryViewState ? undefined : 1}
        flexDirection="column"
        minH={0}
        overflow="hidden"
      >
        {mobileGalleryHeader}
        {pickerContent}
      </Box>
    </Box>
  );

  return {
    configurationPanelBodyWrapperClassName,
    desktopPickerBackRef,
    desktopPickerStepHeader,
    handleCloseSubView,
    handleDrawerCloseComplete,
    inlineStyles,
    isMobile,
    isSubViewOpen,
    mobilePickerPanel,
    panelHeaderInfo,
    pickerContent,
    pickerHeaderClassName,
    sharedContentProps,
    styles,
    handleGalleryFromMain,
    drawerViewportRect,
  };
}

const sessionPropTypes = {
  product: PropTypes.object.isRequired,
  autoCommit: PropTypes.bool,
  coverVariantProduct: PropTypes.object,
  committedOptionProduct: PropTypes.object,
  closePickerRef: PropTypes.shape({current: PropTypes.func}),
  mobileGalleryLayers: PropTypes.array,
  mobileGalleryPreviewProduct: PropTypes.object,
  mobileGalleryVariationAttributes: PropTypes.object,
  onCoverCommitted: PropTypes.func,
  onCoverPickerOpen: PropTypes.func,
  onGalleryProductChange: PropTypes.func,
  onPickerOpenChange: PropTypes.func,
  onSlotProductPreview: PropTypes.func,
  onViewDetails: PropTypes.func,
};

/**
 * Provider for Simple PDP inline customize — mount so desktop picker can render
 * as a sibling outside `data-flyout-frozen` (KL-998 / CoverSwatchOverflowFlyout pattern).
 */
export function CustomizeStepsInlineProvider({children, ...sessionProps}) {
  const session = useCustomizeStepsInlineSession(sessionProps);

  return (
    <CustomizeStepsInlineContext.Provider value={session}>
      {children}
    </CustomizeStepsInlineContext.Provider>
  );
}

CustomizeStepsInlineProvider.displayName = 'CustomizeStepsInlineProvider';
CustomizeStepsInlineProvider.propTypes = {
  children: PropTypes.node.isRequired,
  ...sessionPropTypes,
};

/**
 * Inline slot cards + mobile drawer — desktop picker is {@link CustomizeStepsInlineDesktopPicker}.
 */
export function CustomizeStepsInline() {
  const {
    desktopPickerBackRef,
    handleCloseSubView,
    handleDrawerCloseComplete,
    inlineStyles,
    isMobile,
    isSubViewOpen,
    mobilePickerPanel,
    sharedContentProps,
    styles,
    handleGalleryFromMain,
    drawerViewportRect,
  } = useCustomizeStepsInlineContext();

  return (
    <>
      <Box className="customize-steps-inline-content" {...inlineStyles.content}>
        <CustomizeStepsContent
          {...sharedContentProps}
          contentBodyStyles={inlineStyles.contentBody}
          viewMode="main"
          onGalleryProductChange={handleGalleryFromMain}
        />
      </Box>

      {isMobile ? (
        <Drawer
          blockScrollOnMount={false}
          initialFocusRef={desktopPickerBackRef}
          isOpen={isSubViewOpen}
          placement="bottom"
          returnFocusOnClose={false}
          size="full"
          onClose={handleCloseSubView}
          onCloseComplete={handleDrawerCloseComplete}
        >
          <DrawerOverlay {...styles.overlay} />
          <DrawerContent
            className="configuration-panel__root configuration-panel__root--customize-step"
            {...styles.content}
            {...(drawerViewportRect ?
              {
                bottom: 'auto',
                height: drawerViewportRect.height,
                left: drawerViewportRect.left,
                maxHeight: drawerViewportRect.height,
                position: 'fixed',
                right: 'auto',
                top: drawerViewportRect.top,
                width: drawerViewportRect.width,
              } :
              {})}
          >
            {mobilePickerPanel}
          </DrawerContent>
        </Drawer>
      ) : null}
    </>
  );
}

CustomizeStepsInline.displayName = 'CustomizeStepsInline';

/**
 * Desktop fixed RHS panel — render outside `.variations-section` (sibling under Provider).
 */
export function CustomizeStepsInlineDesktopPicker() {
  const {
    desktopPickerBackRef,
    desktopPickerStepHeader,
    handleCloseSubView,
    isMobile,
    isSubViewOpen,
    panelHeaderInfo,
    pickerContent,
    pickerHeaderClassName,
    styles,
  } = useCustomizeStepsInlineContext();

  const desktopPickerFocusTokenRef = useRef(null);

  const {panelRef} = usePdpConfigPanelA11y({
    focusKey: isSubViewOpen,
    initialFocusRef: desktopPickerBackRef,
    isActive: Boolean(isSubViewOpen && !isMobile),
    onDismiss: handleCloseSubView,
  });

  useLayoutEffect(() => {
    if (isMobile || !isSubViewOpen) {
      desktopPickerFocusTokenRef.current = null;

      return undefined;
    }

    const focusToken = panelHeaderInfo?.title ?? 'open';

    if (desktopPickerFocusTokenRef.current === focusToken) return undefined;

    desktopPickerFocusTokenRef.current = focusToken;
    focusPdpPanelInitialTarget(desktopPickerBackRef, panelRef);

    return undefined;
  }, [desktopPickerBackRef, isMobile, isSubViewOpen, panelHeaderInfo?.title, panelRef]);

  if (isMobile || !isSubViewOpen) {
    return null;
  }

  return (
    <FocusLock returnFocus>
      <Box
        ref={panelRef}
        aria-modal="true"
        bg="white"
        bottom={0}
        className="configuration-selector__desktop-panel customize-steps-inline__desktop-panel"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        position="fixed"
        right={0}
        role="dialog"
        top={0}
        width={`${PDP_DESKTOP_CONFIG_PANEL_WIDTH_PX}px`}
        zIndex={1100}
      >
      <Box
        className={`configuration-selector-panel__header-desktop ${pickerHeaderClassName}`}
        {...styles.desktopPanelHeader}
        gap="16px"
        justifyContent="flex-start"
      >
        {desktopPickerStepHeader}
      </Box>
      <Box
        className="configuration-panel__root configuration-panel__root--customize-step"
        display="flex"
        flex={1}
        flexDirection="column"
        minHeight={0}
        overflow="hidden"
        {...styles.desktopPanel}
      >
        {pickerContent}
      </Box>
    </Box>
    </FocusLock>
  );
}

CustomizeStepsInlineDesktopPicker.displayName = 'CustomizeStepsInlineDesktopPicker';
